import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, PageTitle, TextField } from '@rooming/ui';
import { isProblem } from '@rooming/web-core';
import { useState, type SyntheticEvent } from 'react';
import { passengerApi } from '../api';
import { messageOf } from '../messages';

/** Mandatory change of the initial password (the document number, DR-41). The server enforces the policy (DR-18). */
export function CredentialChangePage({ onSignOut }: { readonly onSignOut: () => void }) {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [fieldError, setFieldError] = useState<string>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const mismatch = repeat !== '' && repeat !== password;

  async function submit(event: SyntheticEvent): Promise<void> {
    event.preventDefault();
    if (mismatch) return;
    setError(undefined);
    setFieldError(undefined);
    setBusy(true);
    try {
      await passengerApi.changeInitialPassword(password);
      setPassword('');
      setRepeat('');
      queryClient.setQueryData(['session'], 'ACTIVE');
    } catch (problem) {
      // Every broken rule, in plain Spanish under the field ("Debe tener al menos 8 caracteres." …), never a generic text.
      const reasons = isProblem(problem)
        ? problem.errors.filter((e) => e.pointer.endsWith('/newPassword')).map((e) => e.detail)
        : [];
      if (reasons.length === 0) setError(messageOf(problem));
      else setFieldError(reasons.join(' '));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-sm">
      <PageTitle subtitle="Por seguridad, reemplazá la contraseña inicial antes de continuar.">
        Elegí tu contraseña
      </PageTitle>
      <Card>
        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)} noValidate>
          <TextField
            label="Nueva contraseña"
            name="newPassword"
            type="password"
            autoComplete="new-password"
            hint="Al menos 8 caracteres. Puede ser una frase. No uses tu documento ni tu usuario."
            required
            value={password}
            error={fieldError}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
          <TextField
            label="Repetí la contraseña"
            name="repeatPassword"
            type="password"
            autoComplete="new-password"
            required
            value={repeat}
            error={mismatch ? 'Las contraseñas no coinciden.' : undefined}
            onChange={(event) => {
              setRepeat(event.target.value);
            }}
          />
          {error !== undefined && <Alert tone="error">{error}</Alert>}
          <Button type="submit" fullWidth loading={busy} disabled={password === '' || repeat === '' || mismatch}>
            Guardar contraseña
          </Button>
          <Button type="button" variant="ghost" fullWidth onClick={onSignOut}>
            Salir
          </Button>
        </form>
      </Card>
    </div>
  );
}
